import React from 'react';
import {FacilityType, FacilityTypeData} from "types/FacilityType";
import IconName from "types/IconName";
import {useOperator} from "context/OperatorContext";
import {Option} from "components/Form/Form/Control";
import {toTitleCase} from "services/string";
import {FormikHelpers} from "formik";
import Form, {ColourField, FormBlock, FormSection, SelectField, TextAreaField, TextField} from "components/Form/Form";
import * as Yup from "yup";

// Types ----

type Props = {
  facilityType?: FacilityType | null
  onSubmit: (values: FacilityTypeData, helpers: FormikHelpers<FacilityTypeData>) => Promise<any>
}

// Helpers ----

const iconOptions: Array<Option> = Object.values(IconName).map(icon => ({
  id: icon, name: toTitleCase(icon), icon: icon
}))

// Component ----

function FacilityTypeForm({facilityType, onSubmit}: Props) {
  const {termFor} = useOperator()

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("This field is required").max(250, "Maximum 250 characters"),
    description: Yup.string().max(20_000, "Maximum character limit exceeded"),
    icon: Yup.string().required("This field is required"),
  })

  const initialValues: FacilityTypeData = {
    name: facilityType?.name ?? '',
    description: facilityType?.description ?? '',
    colour: facilityType?.colour ?? '#2b2d30',
    icon: facilityType?.icon ?? IconName.INFORMATION_CIRCLE,
  }

  return (
    <Form initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
      <FormSection
        name="Key Information"
        description={`Basic details about the ${termFor('facility type')}`}
        columns={6}
      >
        {/* Name */}
        <FormBlock columns={3}>
          <TextField
            name="name"
            label="Name *"
            hint="250 chars max"
            help={`The display name for this ${termFor('facility type')}`}
            autoComplete="off"
            maxLength={250}
          />
        </FormBlock>

        {/* Icon */}
        <FormBlock columns={2}>
          <SelectField
            name="icon"
            label="Icon *"
            help="The icon shown with the name"
            options={iconOptions}
          />
        </FormBlock>

        {/* Description */}
        <FormBlock columns={6}>
          <TextAreaField
            name="description"
            label="Description"
            hint="Optional. 20,000 chars max."
            help={`Additional information about this ${termFor('facility type')}`}
            autoComplete="off"
            maxChars={20_000}
            rows={5}
          />
        </FormBlock>

        {/* Colour */}
        <FormBlock>
          <ColourField name="colour" label="Colour" />
        </FormBlock>
      </FormSection>
    </Form>
  )
}

export default FacilityTypeForm;